import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${storedToken}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setOrganization(data.organization);
        setToken(storedToken);
      } else {
        localStorage.removeItem('auth_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Use response.json() directly - simpler and more reliable
      // The previous approach with text() was causing issues with fetch interceptors
      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If JSON parsing fails, the response might be empty or invalid
        if (!response.ok) {
          throw new Error('Invalid credentials');
        }
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }

      localStorage.setItem('auth_token', data.token);
      setUser(data.user);
      setToken(data.token);
      
      if (data.organization) {
        setOrganization(data.organization);
      }
      
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      // Re-throw with a clean error message
      if (error.message && (error.message.includes('body stream') || error.message.includes('clone'))) {
        throw new Error('Invalid credentials');
      }
      throw error;
    }
  };

  const register = async (name, email, password) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        if (!response.ok) {
          throw new Error('Registration failed');
        }
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      localStorage.setItem('auth_token', data.token);
      setUser(data.user);
      setToken(data.token);
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message && (error.message.includes('body stream') || error.message.includes('clone'))) {
        throw new Error('Registration failed');
      }
      throw error;
    }
  };

  const processGoogleSession = async (sessionId) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        if (!response.ok) {
          throw new Error('Failed to process session');
        }
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to process session');
      }

      localStorage.setItem('auth_token', data.token);
      setUser(data.user);
      setToken(data.token);
      
      // Set organization from response if available
      if (data.organization) {
        setOrganization(data.organization);
      }
      
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message && (error.message.includes('body stream') || error.message.includes('clone'))) {
        throw new Error('Failed to process session');
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('auth_token');
    setUser(null);
    setOrganization(null);
    setToken(null);
  };

  const refreshOrganization = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/organizations/mine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setOrganization(data);
      }
    } catch (error) {
      console.error('Failed to refresh organization:', error);
    }
  };

  const value = {
    user,
    organization,
    token,
    loading,
    login,
    register,
    logout,
    processGoogleSession,
    refreshOrganization,
    setOrganization
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
